// Shop (merchant) inventory generator. Step 10c-shop wires the deterministic
// `shopSeed` channel + per-Tier rarity weights into @renavora/content's
// `generateItem` to produce a fixed merchant stock for a camp/shop node.
//
// Mirrors dungeon-loot.ts (boss-clear loot): same ItemGenContext shape and
// ilvl ceiling. It differs in that each offer forces a rarity rolled from the
// shop weight table (ADR 0026 §2.8) and carries a derived gold price
// (`shopItemPrice`).
//
// Determinism: keyed on `shopSeed(mapSeed, coord)` so re-opening the modal —
// or `resolveShopPurchase` regenerating the stock to validate a purchase —
// always reproduces the same offers. Stock never rerolls (ADR 0026 §2.8). The
// channel is independent from the loot / boon / event / subtype channels.
//
// Phase 3 Step 10c-shop.

import {
  Rng,
  rollShopOfferCount,
  rollShopRarity,
  shopItemPrice,
  shopSeed,
  type DungeonTemplate,
  type NodeCoord,
  type ShopOffer,
} from '@renavora/shared';
import { generateItem } from '@renavora/content';

const ILVL_CEILING = 500;

/**
 * Generate the merchant stock for a shop node: 4-6 offers, each a
 * `generateItem` result with a forced rarity and a derived price.
 *
 * `mapSeed` is the numeric seed parsed from `DungeonRunState.mapId` via
 * `parseMapId(...).seed`; `coord` is the node's `[floor, index]` (the two
 * Floor-6 shop nodes stock different inventory because the seed is
 * coord-keyed). The draw order — one offer-count roll, then per offer a
 * rarity roll followed by the `generateItem` draws — is fixed so callers
 * regenerating the stock land on the same items.
 */
export function generateShopInventory(
  template: DungeonTemplate,
  mapSeed: number,
  coord: NodeCoord,
): ShopOffer[] {
  const rng = Rng.fromSeed(shopSeed(mapSeed, coord));
  const count = rollShopOfferCount(rng);
  const difficultyIlvl = Math.min(ILVL_CEILING, template.baseEnemyLevel);
  const offers: ShopOffer[] = [];
  for (let i = 0; i < count; i++) {
    const forcedRarity = rollShopRarity(template.tier, rng);
    const item = generateItem(
      { dungeonTier: template.tier, difficultyIlvl, forcedRarity },
      rng,
    );
    offers.push({ item, price: shopItemPrice(item) });
  }
  return offers;
}
